import dayjs from 'dayjs';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ChartHeader from 'components/charts/ChartHeader';
import CompositeFilter, { ANALYSIS_FILTERS, getActiveFilters } from 'components/charts/CompositeFilter';
import BarChartV2 from 'components/charts/chartJS/BarChart';
import { chartColors } from 'components/charts/chartJS/utils';
import { Divider, DynamicListChartContainer, FiltersContainer } from 'components/charts/styled/Chart.styled';
import Button from 'components/field/Button';
import DateFilter from 'components/field/DateFilter';
import Card from 'components/global/Card';
import useDefaultFiltersSync from 'components/global/ManageWidgets/useDefaultFiltersSync';
import { calcultateCustomWidgetDateFilter } from 'components/global/ManageWidgets/widgetsUtils';
import WidgetPageContainer from 'components/global/WidgetPageContainer';
import WidgetWrapper from 'components/global/WidgetWrapper';
import DynamicList from 'components/list/DynamicList';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useBrandManagerFilter from 'hooks/useBrandManagerFilter';
import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { ENUMS, REPORT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import * as S from './DeliveryCompany.styled';

const DeliveryCompanyTab = () => (
	<WidgetPageContainer>
		<WidgetWrapper widgetId={'dcPerformance'}>
			<DCPerformanceList />
		</WidgetWrapper>
		<WidgetWrapper widgetId={'deliveryCompDistribution'}>
			<Divider />
			<DeliveryCompDistribution />
		</WidgetWrapper>
	</WidgetPageContainer>
);

export const DCPerformanceList = ({ widgetName, onApplyFilters, defaultFilters }) => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const { userType } = useSelector((state) => state.user);
	const isBrandManager = userType === 'BrandManager';

	const driverPerformanceColumns = [
		{
			Header: 'name',
			accessor: 'name',
			width: 100,
			filterType: FilterType.string,
			showOnMobile: true
		},
		{
			Header: 'contractType',
			accessor: 'contractType',
			width: 140,
			filterType: FilterType.enumMulti,
			enumName: 'ClientAgreementType',
			renderCell: ListComponents.CLIENT_AGREEMENT_TYPE
		},
		{ Header: 'totalCount', accessor: 'totalCount', width: 100, filterType: FilterType.number },
		{
			Header: 'share',
			accessor: 'Share',
			width: 100,
			filterType: FilterType.float,
			renderCell: ListComponents.PERCENTAGE
		},
		{ Header: 'pickedUpShipments', accessor: 'pickedUpShipments', width: 100, filterType: FilterType.string },
		{ Header: 'deliveredShipments', accessor: 'deliveredShipments', width: 100, filterType: FilterType.string },
		{ Header: 'avgPickupTime', accessor: 'Average_Pickup_Time', width: 100, filterType: FilterType.string },
		{ Header: 'avgDeliveryTime', accessor: 'Average_Delivery_Time', width: 100, filterType: FilterType.string },
		{
			Header: 'Return_Rate',
			accessor: 'Return_Rate',
			width: 100,
			filterType: FilterType.float,
			renderCell: ListComponents.PERCENTAGE
		},
		{
			Header: 'brand',
			accessor: 'brandName',
			width: 100,
			filterType: FilterType.enumMulti,
			enumName: ENUMS.ASSIGNED_BRANDS
		}
	];

	const [defaultFiltersLocal, setDefaultFiltersLocal] = useState([
		{
			column: {
				Header: 'createdAt',
				accessor: 'createdAt',
				filterType: FilterType.date
			},
			action: 'between',
			value: filter
		}
	]);

	useEffect(() => {
		const newDefaultFilters = [
			{
				column: {
					Header: 'createdAt',
					accessor: 'createdAt',
					filterType: FilterType.date
				},
				action: 'between',
				value: filter
			}
		];
		setDefaultFiltersLocal(newDefaultFilters);
	}, [filter]);

	useEffect(() => {
		if (defaultFilters && Array.isArray(defaultFilters)) {
			setDefaultFiltersLocal(defaultFilters);
		}
	}, [defaultFilters]);

	const lastAppliedFiltersRef = useRef(defaultFilters);

	useEffect(() => {
		const handleStorage = () => {
			try {
				const stored = localStorage.getItem(`${DYNAMICLISTS.DC_PERFORMANCE}_filters`);
				if (!stored) return;
				const localStorageFilters = JSON.parse(stored);
				const defaultFiltersString = JSON.stringify(defaultFilters);
				const lastAppliedString = JSON.stringify(lastAppliedFiltersRef.current);

				if (stored !== defaultFiltersString && stored !== lastAppliedString) {
					onApplyFilters?.(localStorageFilters);
					lastAppliedFiltersRef.current = localStorageFilters;
				}
			} catch {
				AppLogger.error('Failed to handle storage event in DCPerformanceList');
			}
		};

		window.addEventListener('storage', handleStorage);
		const interval = setInterval(handleStorage, 1000);

		return () => {
			window.removeEventListener('storage', handleStorage);
			clearInterval(interval);
		};
	}, [defaultFilters, onApplyFilters]);

	return (
		<Card>
			<ChartHeader
				title={widgetName || 'dcPerformance'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
				titleTooltip="dcPerformanceTooltip"
			/>
			<DynamicListChartContainer>
				<DynamicList
					id={DYNAMICLISTS.DC_PERFORMANCE}
					className="importExportListing"
					columns={driverPerformanceColumns}
					exportName="dcPerformance"
					showHeader={false}
					showFilters={false}
					fetchUrl={
						isBrandManager
							? REPORT.DELIVERY_COMPANY.GET_DC_PERFORMANCE_BM
							: REPORT.DELIVERY_COMPANY.GET_DC_PERFORMANCE
					}
					showColumnCalculation={false}
					defaultFilters={defaultFiltersLocal}
				/>
			</DynamicListChartContainer>
		</Card>
	);
};

export const DeliveryCompDistribution = ({ widgetName, onApplyFilters, defaultFilters }) => {
	const filtersList = [
		ANALYSIS_FILTERS.destinationCountry,
		ANALYSIS_FILTERS.destinationCity,
		ANALYSIS_FILTERS.warehouse,
		ANALYSIS_FILTERS.branch,
		ANALYSIS_FILTERS.brand,
		ANALYSIS_FILTERS.salesChannel,
		ANALYSIS_FILTERS.paymentMethod
	];

	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);
	const [extraFilters, setExtraFilters] = useState(filtersList);
	const [filter, setFilter] = useState({
		start: dayjs().subtract(29, 'day').startOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});
	const { userType, assignedBrands } = useSelector((state) => state.user);
	const isBrandManager = userType === 'BrandManager';

	useBrandManagerFilter({ isBrandManager, assignedBrands, setExtraFilters });

	const body = useMemo(
		() => ({
			from: filter.start,
			to: filter.end,
			filter: getActiveFilters(extraFilters)
		}),
		[filter, extraFilters]
	);

	useDefaultFiltersSync({
		defaultFilters,
		filter,
		setFilter,
		extraFilters,
		setExtraFilters,
		getActiveFilters,
		body,
		onApplyFilters
	});

	const handleDefaultValue = useCallback(() => {
		return calcultateCustomWidgetDateFilter(defaultFilters);
	}, [defaultFilters]);

	const afterFetch = (data) => {
		data.sort((a, b) => b.value - a.value);
		const labels = data.map((item) => item.label);
		const datasets = [{ data: data.map((item) => item.value), ...chartColors }];
		return { labels, datasets };
	};
	const { ref, data, loading } = useFetchAndParseOnView({
		url: REPORT.ANALYSIS.GET_DELIVERY_COMPANY_DISTRIBUTION,
		body,
		parseData: afterFetch
	});

	return (
		<S.ChartCard ref={ref}>
			<ChartHeader
				title={widgetName || 'deliveryCompDistribution'}
				filter={
					<FiltersContainer>
						<Button
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							label="filters"
							variant="borderless"
							size="small"
							className="py-0"
						/>
						<CompositeFilter
							filtersMenuButtonRef={filtersMenuButtonRef}
							filtersList={extraFilters}
							setFiltersList={setExtraFilters}
						/>
						<DateFilter
							defaultDay={handleDefaultValue()?.defaultDay || 'last30Days'}
							setValue={setFilter}
							defaultFilter={handleDefaultValue()?.defaultFilter || null}
						/>
					</FiltersContainer>
				}
				titleTooltip="deliveryCompDistributionTooltip"
			/>
			<BarChartV2 data={data} loading={loading} showLegend />
		</S.ChartCard>
	);
};

export default DeliveryCompanyTab;
